import React, {Fragment, useState} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import POSState from 'features/POSRegisters/interfaces/POSState';
import {
  hasAssociation,
  checkIsScanFailureAlert,
  checkIsOrphanDeviceAlert,
  checkIsConfigurationDeviceAlert,
} from 'features/POSRegisters/functions/alertTypes';
import Alert from 'features/POSRegisters/components/Alert';
import {DeviceTypeString} from 'features/POSRegisters/interfaces/DeviceType';
import DeviceModal from 'features/POSRegisters/components/DeviceModal';
import ModalButton from 'features/POSRegisters/components/ModalButton';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import TypedDevice from 'features/POSRegisters/interfaces/TypedDevice';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import AlertType from 'features/POSRegisters/interfaces/AlertInfo';

//#region styles
const StyledContainer = styled(Grid)`
  padding: 8px;
  gap: 8px;

  > .MuiPaper-root {
    background: transparent;
  }

  .MuiTableCell-root.MuiTableCell-head {
    padding-top: 4px;
    padding-bottom: 0;
  }

  .MuiTableCell-root.MuiTableCell-head span {
    font-size: 12px;
  }

  .MuiTableCell-root.MuiTableCell-body p {
    font-size: 16px;
  }
`;

const StyledAnomalyAlerts = styled.div`
  width: 100%;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

//#endregion styles

export default function Anomalies(state: POSState) {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [selectedTypedDevice, setSelectedTypedDevice] = useState<TypedDevice<ScanDetails>>();
  const devices = [
    ...state.ipads?.map(device => ({...device, type: 'ipad' as DeviceType})),
    ...state.ipadsMini?.map(device => ({...device, type: 'ipadMini' as DeviceType})),
    ...state.receiptPrinters?.map(device => ({...device, type: 'receiptPrinter' as DeviceType})),
    ...state.cashDrawers?.map(device => ({...device, type: 'cashDrawer' as DeviceType})),
    ...state.barcodeScanners?.map(device => ({...device, type: 'barcodeScanner' as DeviceType})),
    ...state.paymentTerminals?.map(device => ({...device, type: 'paymentTerminal' as DeviceType})),
  ].filter(device => device?.alerts?.length);

  const getAnomalies = (device: Device<ScanDetails>, checkFn: (alert: AlertType) => boolean) =>
    device?.alerts?.filter(alert => checkFn(alert) && !hasAssociation(device?.scanResults));

  const hasAnomalies = devices.some(device => {
    return device.alerts.some((alert: AlertType) => {
      return (
        !hasAssociation(device?.scanResults) &&
        (checkIsScanFailureAlert(alert) ||
          checkIsOrphanDeviceAlert(alert) ||
          checkIsConfigurationDeviceAlert(alert))
      );
    });
  });

  const handleOpen = (deviceType: DeviceType, selectedTypedDevice: TypedDevice<ScanDetails>) => {
    setOpenModal(true);
    setSelectedDevice(deviceType);
    setSelectedTypedDevice(selectedTypedDevice);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <StyledContainer container>
      <StyledAnomalyAlerts>
        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`scan-failure-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsScanFailureAlert).map((alert, alertIdx) => (
              <Alert
                key={`scan-failure-alerts-${deviceIdx}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}

        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`orphan-devices-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsOrphanDeviceAlert).map((alert, alertIdx) => (
              <Alert
                key={`orphan-devices-alerts-${device.type}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    value: (
                      <ModalButton onClick={() => handleOpen(device.type, device)}>
                        VIEW DEVICE INFORMATION
                      </ModalButton>
                    ),
                    fullWidth: true,
                  },
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}

        {devices.map((device, deviceIdx) => (
          <Fragment
            key={`configuration-${device.type}-${device.scanResults?.deviceName}-${deviceIdx}`}
          >
            {getAnomalies(device, checkIsConfigurationDeviceAlert).map((alert, alertIdx) => (
              <Alert
                key={`configuration-alerts-${deviceIdx}-${device.scanResults?.deviceName}-${alertIdx}`}
                device={device}
                alert={alert}
                rows={[
                  {
                    label: 'Device Type',
                    value: DeviceTypeString[device.type],
                  },
                  {
                    label: 'Device Name',
                    value: device.scanResults.deviceName,
                  },
                  {label: 'Model', value: device.scanResults.model},
                  {label: 'Serial Number', value: device.scanResults.serialNumber},
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            ))}
          </Fragment>
        ))}
      </StyledAnomalyAlerts>
      <DeviceModal
        selectedDevice={selectedDevice}
        modalState={openModal}
        handleClose={handleClose}
        typedDevice={selectedTypedDevice}
      />
    </StyledContainer>
  );
}
